import { Button } from '@/core/ui/components';
import { useBillingPlans } from '@/modules/billing/ui/hooks/useBilling';
import { Building2, Check, CreditCard, Edit2, Palette, Settings, UserCog } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import type { WizardFormData } from '../types';

interface SummaryStepProps {
  formData: WizardFormData;
  onChange: (data: Partial<WizardFormData>) => void;
  onValidChange: (isValid: boolean) => void;
  onGoToStep: (step: number) => void;
  errors: Record<string, string>;
}

const LANGUAGE_LABELS: Record<string, string> = {
  es: 'Spanish',
  en: 'English',
  pt: 'Portuguese',
};

export function SummaryStep({ formData, onValidChange, onGoToStep }: SummaryStepProps) {
  const { plans } = useBillingPlans();

  const selectedPlan = useMemo(() => {
    return plans.find((p) => p.id === formData.planId);
  }, [plans, formData.planId]);

  useEffect(() => {
    // Summary is always valid (it's readonly)
    onValidChange(true);
  }, [onValidChange]);

  const sections = [
    {
      title: 'Basic Information',
      icon: Building2,
      step: 0,
      items: [
        { label: 'Tenant Name', value: formData.name },
        { label: 'URL', value: `${formData.slug}.hommi.mx` },
      ],
    },
    {
      title: 'Admin Account',
      icon: UserCog,
      step: 1,
      items: [
        { label: 'Admin Name', value: formData.adminName },
        { label: 'Admin Email', value: formData.adminEmail },
      ],
    },
    {
      title: 'Plan',
      icon: CreditCard,
      step: 2,
      items: [{ label: 'Selected Plan', value: selectedPlan?.name || 'Not selected' }],
    },
    {
      title: 'Branding',
      icon: Palette,
      step: 3,
      items: [
        { label: 'Logo', value: formData.branding.logoUrl || 'Not set' },
        {
          label: 'Colors',
          value: formData.branding.primaryColor
            ? `${formData.branding.primaryColor} / ${formData.branding.secondaryColor}`
            : 'Default',
          colors: formData.branding.primaryColor
            ? [formData.branding.primaryColor, formData.branding.secondaryColor]
            : null,
        },
      ],
    },
    {
      title: 'Settings',
      icon: Settings,
      step: 4,
      items: [
        { label: 'Timezone', value: formData.settings.timezone },
        { label: 'Language', value: LANGUAGE_LABELS[formData.settings.language] || formData.settings.language },
        { label: 'Currency', value: formData.settings.currency },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
          <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-lg font-semibold text-theme">Review & Confirm</h3>
        <p className="text-sm text-theme-secondary mt-1">
          Please review the information below before creating the tenant
        </p>
      </div>

      <div className="space-y-4">
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <div
              key={section.title}
              className="p-4 rounded-lg border border-theme bg-surface"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <h4 className="font-medium text-theme">{section.title}</h4>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onGoToStep(section.step)}
                  leftIcon={<Edit2 className="w-3 h-3" />}
                >
                  Edit
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.items.map((item, idx) => (
                  <div key={idx} className="flex flex-col">
                    <span className="text-xs text-theme-tertiary">{item.label}</span>
                    <div className="flex items-center gap-2">
                      {item.colors ? (
                        <>
                          <div
                            className="w-4 h-4 rounded-full border border-theme"
                            style={{ backgroundColor: item.colors[0] }}
                          />
                          <div
                            className="w-4 h-4 rounded-full border border-theme"
                            style={{ backgroundColor: item.colors[1] }}
                          />
                          <span className="text-sm text-theme">{item.value}</span>
                        </>
                      ) : (
                        <span className="text-sm font-medium text-theme">{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
        <p className="text-sm text-indigo-700 dark:text-indigo-300">
          <strong>Note:</strong> After creating the tenant, an invitation email will be sent to{' '}
          <span className="font-medium">{formData.adminEmail}</span> with instructions to set up their account.
        </p>
      </div>
    </div>
  );
}
